// client/src/realtime/useRealtimeCacheUpdates.ts
// Hook that connects realtime events to RTK Query cache updates

import { useEffect } from 'react';
import { useAppDispatch } from '../store/store';
import { tasksApi } from '../api/tasksApi';
import { opportunitiesApi } from '../api/opportunitiesApi';
import { conversationsApi } from '../api/conversationsApi';
import { assistantApi } from '../api/assistantApi';
import { telephonyApi } from '../api/telephonyApi';
// PR-27.2 — invalidate the Contacts list when CRM emits a contact
// realtime event. Without this, a sibling-app sync (Outbound, Scheduler,
// WeOpen) writes to the contacts table without the CRM tab knowing,
// so the user has to manually refresh.
import { contactsApi } from '../api/contactsApi';
// Phase 4 — Providers page + pre-call selector need instant refresh
// when the Outbound → CRM mirror upserts/deletes a provider row.
import { integrationsApi } from '../api/integrationsApi';
// Phase 12.4 — Circle Providers tab invalidates on affinity changes.
import { circlesApi } from '../api/circlesApi';
import { realtimeClient } from './socketClient';
import {
  RealtimeEventType,
  TaskPayload,
  TaskDeletedPayload,
  OpportunityPayload,
  OpportunityDeletedPayload,
  ConversationMessagePayload,
  TimeEntryDeletedPayload,
  ActionPayload,
  ActionPlannedPayload,
  TaskChecklistItemPayload,
  TaskChecklistItemDeletedPayload,
  TaskChecklistItemToggledPayload,
  TaskTemplatePayload,
  TaskTemplateDeletedPayload,
} from './events';
import { taskTemplatesApi } from '../api/taskTemplatesApi';
import type {
  Task,
  Opportunity,
  ConversationMessage,
  ConversationAction,
  TaskChecklistItem,
  TaskTemplate,
} from '../types/api';

// ============================================================
// Type-Safe Helper Functions for Mapping Payloads
// ============================================================

function mapPayloadToTask(payload: TaskPayload): Task {
  return {
    id: payload.task.id,
    workspaceId: payload.task.workspaceId,
    title: payload.task.title,
    description: payload.task.description,
    status: payload.task.status as Task['status'],
    priority: payload.task.priority as Task['priority'],
    dueDate: payload.task.dueDate,
    assigneeUserId: payload.task.assigneeUserId,
    createdByUserId: payload.task.createdByUserId,
    relatedContactId: null,
    tags: null,
    createdAt: payload.task.createdAt,
    updatedAt: payload.task.updatedAt,
  };
}

function mapPayloadToOpportunity(payload: OpportunityPayload): Opportunity {
  return {
    id: payload.opportunity.id,
    workspaceId: payload.opportunity.workspaceId,
    pipelineId: payload.opportunity.pipelineId,
    stageId: payload.opportunity.stageId,
    title: payload.opportunity.title,
    amount: payload.opportunity.amount,
    currency: payload.opportunity.currency,
    status: payload.opportunity.status as Opportunity['status'],
    expectedCloseDate: payload.opportunity.expectedCloseDate,
    contactId: payload.opportunity.contactId,
    assignedToUserId: payload.opportunity.assignedToUserId,
    createdAt: payload.opportunity.createdAt,
    updatedAt: payload.opportunity.updatedAt,
  } as Opportunity;
}

function mapPayloadToMessage(payload: ConversationMessagePayload): ConversationMessage {
  return {
    id: payload.message.id,
    sessionId: payload.message.sessionId,
    senderType: payload.message.senderType as ConversationMessage['senderType'],
    senderUserId: payload.message.senderUserId,
    contactId: null,
    content: payload.message.content,
    metadata: null,
    createdAt: payload.message.createdAt,
  };
}

function mapActionPayloadToAction(action: ActionPayload['action']): ConversationAction {
  return {
    id: action.id,
    sessionId: action.conversationId,
    conversationId: action.conversationId,
    workspaceId: action.workspaceId,
    actionType: action.actionType as ConversationAction['actionType'],
    actionStatus: action.status as ConversationAction['actionStatus'],
    targetType: action.targetType,
    targetId: action.targetId,
    summary: action.summary,
    payload: action.payload,
    errorMessage: action.errorMessage,
    suggestedBy: action.suggestedBy as ConversationAction['suggestedBy'],
    requiresConfirmation: action.requiresConfirmation,
    explanation: action.explanation,
    confidence: action.confidence,
    createdAt: action.createdAt,
    updatedAt: action.updatedAt,
  };
}

// ============================================================
// Workspace-level Cache Updates Hook
// ============================================================

/**
 * Hook that listens for realtime events and updates RTK Query caches.
 * Call this once at the app level after authentication.
 */
export function useRealtimeCacheUpdates(workspaceId: string | null | undefined) {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!workspaceId) return;

    const topic = `workspace:${workspaceId}`;
    const unsubscribers: Array<() => void> = [];

    // ========== TASK EVENTS ==========

    // Task created
    unsubscribers.push(
      realtimeClient.on<TaskPayload>(
        topic,
        (payload) => {
          const newTask = mapPayloadToTask(payload);

          // Update task list queries
          dispatch(
            tasksApi.util.updateQueryData('getTasks', { workspaceId }, (draft) => {
              if (draft?.tasks && !draft.tasks.find((t) => t.id === newTask.id)) {
                draft.tasks.unshift(newTask);
              }
            })
          );
        },
        RealtimeEventType.TASK_CREATED
      )
    );

    // Task updated
    unsubscribers.push(
      realtimeClient.on<TaskPayload>(
        topic,
        (payload) => {
          const updatedTask = mapPayloadToTask(payload);

          // Update task list
          dispatch(
            tasksApi.util.updateQueryData('getTasks', { workspaceId }, (draft) => {
              if (draft?.tasks) {
                const index = draft.tasks.findIndex((t) => t.id === updatedTask.id);
                if (index !== -1) {
                  draft.tasks[index] = updatedTask;
                }
              }
            })
          );

          // Update individual task query
          dispatch(
            tasksApi.util.updateQueryData('getTask', updatedTask.id, () => updatedTask)
          );
        },
        RealtimeEventType.TASK_UPDATED
      )
    );

    // Task deleted
    unsubscribers.push(
      realtimeClient.on<TaskDeletedPayload>(
        topic,
        (payload) => {
          // Remove from task list
          dispatch(
            tasksApi.util.updateQueryData('getTasks', { workspaceId }, (draft) => {
              if (draft?.tasks) {
                const index = draft.tasks.findIndex((t) => t.id === payload.taskId);
                if (index !== -1) {
                  draft.tasks.splice(index, 1);
                }
              }
            })
          );
        },
        RealtimeEventType.TASK_DELETED
      )
    );

    // ========== TASK CHECKLIST EVENTS ==========

    // Checklist item created
    unsubscribers.push(
      realtimeClient.on<TaskChecklistItemPayload>(
        topic,
        (payload) => {
          const newItem: TaskChecklistItem = {
            id: payload.item.id,
            taskId: payload.item.taskId,
            title: payload.item.title,
            completed: payload.item.completed,
            completedAt: payload.item.completedAt,
            completedByUserId: payload.item.completedByUserId,
            position: payload.item.position,
            createdAt: payload.item.createdAt,
            updatedAt: payload.item.updatedAt,
          };

          dispatch(
            tasksApi.util.updateQueryData('getChecklist', payload.taskId, (draft) => {
              if (draft && !draft.find((i) => i.id === newItem.id)) {
                draft.push(newItem);
                // Sort by position
                draft.sort((a, b) => a.position - b.position);
              }
            })
          );
        },
        RealtimeEventType.TASK_CHECKLIST_ITEM_CREATED
      )
    );

    // Checklist item updated
    unsubscribers.push(
      realtimeClient.on<TaskChecklistItemPayload>(
        topic,
        (payload) => {
          const updatedItem: TaskChecklistItem = {
            id: payload.item.id,
            taskId: payload.item.taskId,
            title: payload.item.title,
            completed: payload.item.completed,
            completedAt: payload.item.completedAt,
            completedByUserId: payload.item.completedByUserId,
            position: payload.item.position,
            createdAt: payload.item.createdAt,
            updatedAt: payload.item.updatedAt,
          };

          dispatch(
            tasksApi.util.updateQueryData('getChecklist', payload.taskId, (draft) => {
              if (draft) {
                const index = draft.findIndex((i) => i.id === updatedItem.id);
                if (index !== -1) {
                  draft[index] = updatedItem;
                  draft.sort((a, b) => a.position - b.position);
                }
              }
            })
          );
        },
        RealtimeEventType.TASK_CHECKLIST_ITEM_UPDATED
      )
    );

    // Checklist item toggled
    unsubscribers.push(
      realtimeClient.on<TaskChecklistItemToggledPayload>(
        topic,
        (payload) => {
          dispatch(
            tasksApi.util.updateQueryData('getChecklist', payload.taskId, (draft) => {
              if (draft) {
                const item = draft.find((i) => i.id === payload.checklistItemId);
                if (item) {
                  item.completed = payload.completed;
                  item.completedAt = payload.completedAt;
                  item.completedByUserId = payload.completedByUserId;
                }
              }
            })
          );
        },
        RealtimeEventType.TASK_CHECKLIST_ITEM_TOGGLED
      )
    );

    // Checklist item deleted
    unsubscribers.push(
      realtimeClient.on<TaskChecklistItemDeletedPayload>(
        topic,
        (payload) => {
          dispatch(
            tasksApi.util.updateQueryData('getChecklist', payload.taskId, (draft) => {
              if (draft) {
                const index = draft.findIndex((i) => i.id === payload.checklistItemId);
                if (index !== -1) {
                  draft.splice(index, 1);
                }
              }
            })
          );
        },
        RealtimeEventType.TASK_CHECKLIST_ITEM_DELETED
      )
    );

    // ========== TASK TEMPLATE EVENTS ==========

    // Template created
    unsubscribers.push(
      realtimeClient.on<TaskTemplatePayload>(
        topic,
        (payload) => {
          const newTemplate: TaskTemplate = {
            id: payload.template.id,
            workspaceId: payload.template.workspaceId,
            title: payload.template.title,
            description: payload.template.description,
            priority: payload.template.priority as TaskTemplate['priority'],
            assigneeUserIds: payload.template.assigneeUserIds,
            relatedContactId: payload.template.relatedContactId,
            goalId: payload.template.goalId,
            tags: payload.template.tags,
            checklistTemplate: null,
            recurrenceRule: payload.template.recurrenceRule,
            startAt: payload.template.startAt,
            timezone: payload.template.timezone,
            isActive: payload.template.isActive,
            lastGeneratedAt: payload.template.lastGeneratedAt,
            nextOccurrenceAt: payload.template.nextOccurrenceAt,
            createdByUserId: payload.template.createdByUserId,
            createdAt: payload.template.createdAt,
            updatedAt: payload.template.updatedAt,
          };

          dispatch(
            taskTemplatesApi.util.updateQueryData('getTaskTemplates', { workspaceId }, (draft) => {
              if (draft && !draft.find((t) => t.id === newTemplate.id)) {
                draft.unshift(newTemplate);
              }
            })
          );
        },
        RealtimeEventType.TASK_TEMPLATE_CREATED
      )
    );

    // Template updated, paused, resumed (all use same payload shape)
    const templateUpdateEvents = [
      RealtimeEventType.TASK_TEMPLATE_UPDATED,
      RealtimeEventType.TASK_TEMPLATE_PAUSED,
      RealtimeEventType.TASK_TEMPLATE_RESUMED,
    ] as const;

    templateUpdateEvents.forEach((eventType) => {
      unsubscribers.push(
        realtimeClient.on<TaskTemplatePayload>(
          topic,
          (payload) => {
            const updatedTemplate: TaskTemplate = {
              id: payload.template.id,
              workspaceId: payload.template.workspaceId,
              title: payload.template.title,
              description: payload.template.description,
              priority: payload.template.priority as TaskTemplate['priority'],
              assigneeUserIds: payload.template.assigneeUserIds,
              relatedContactId: payload.template.relatedContactId,
              goalId: payload.template.goalId,
              tags: payload.template.tags,
              checklistTemplate: null,
              recurrenceRule: payload.template.recurrenceRule,
              startAt: payload.template.startAt,
              timezone: payload.template.timezone,
              isActive: payload.template.isActive,
              lastGeneratedAt: payload.template.lastGeneratedAt,
              nextOccurrenceAt: payload.template.nextOccurrenceAt,
              createdByUserId: payload.template.createdByUserId,
              createdAt: payload.template.createdAt,
              updatedAt: payload.template.updatedAt,
            };

            // Update list queries
            dispatch(
              taskTemplatesApi.util.updateQueryData('getTaskTemplates', { workspaceId }, (draft) => {
                if (draft) {
                  const index = draft.findIndex((t) => t.id === updatedTemplate.id);
                  if (index !== -1) {
                    draft[index] = updatedTemplate;
                  }
                }
              })
            );

            // Update individual query
            dispatch(
              taskTemplatesApi.util.updateQueryData('getTaskTemplate', updatedTemplate.id, () => updatedTemplate)
            );
          },
          eventType
        )
      );
    });

    // Template deleted
    unsubscribers.push(
      realtimeClient.on<TaskTemplateDeletedPayload>(
        topic,
        (payload) => {
          dispatch(
            taskTemplatesApi.util.updateQueryData('getTaskTemplates', { workspaceId }, (draft) => {
              if (draft) {
                const index = draft.findIndex((t) => t.id === payload.templateId);
                if (index !== -1) {
                  draft.splice(index, 1);
                }
              }
            })
          );
        },
        RealtimeEventType.TASK_TEMPLATE_DELETED
      )
    );

    // ========== OPPORTUNITY EVENTS ==========

    // Opportunity created
    unsubscribers.push(
      realtimeClient.on<OpportunityPayload>(
        topic,
        (payload) => {
          const newOpp = mapPayloadToOpportunity(payload);

          // Update opportunity list queries (workspace-level)
          dispatch(
            opportunitiesApi.util.updateQueryData(
              'getOpportunities',
              { workspaceId },
              (draft) => {
                if (draft && !draft.find((o) => o.id === newOpp.id)) {
                  draft.unshift(newOpp);
                }
              }
            )
          );

          // Also update pipeline-specific query
          dispatch(
            opportunitiesApi.util.updateQueryData(
              'getOpportunities',
              { workspaceId, pipelineId: payload.pipelineId },
              (draft) => {
                if (draft && !draft.find((o) => o.id === newOpp.id)) {
                  draft.unshift(newOpp);
                }
              }
            )
          );
        },
        RealtimeEventType.OPPORTUNITY_CREATED
      )
    );

    // Opportunity updated
    unsubscribers.push(
      realtimeClient.on<OpportunityPayload>(
        topic,
        (payload) => {
          const updatedOpp = mapPayloadToOpportunity(payload);

          // Update opportunity list
          dispatch(
            opportunitiesApi.util.updateQueryData(
              'getOpportunities',
              { workspaceId },
              (draft) => {
                if (draft) {
                  const index = draft.findIndex((o) => o.id === updatedOpp.id);
                  if (index !== -1) {
                    draft[index] = updatedOpp;
                  }
                }
              }
            )
          );

          // Update individual opportunity query
          dispatch(
            opportunitiesApi.util.updateQueryData('getOpportunity', updatedOpp.id, () => updatedOpp)
          );
        },
        RealtimeEventType.OPPORTUNITY_UPDATED
      )
    );

    // Opportunity stage changed
    unsubscribers.push(
      realtimeClient.on<OpportunityPayload>(
        topic,
        (payload) => {
          const updatedOpp = mapPayloadToOpportunity(payload);

          // Update all relevant opportunity list queries
          dispatch(
            opportunitiesApi.util.updateQueryData(
              'getOpportunities',
              { workspaceId },
              (draft) => {
                if (draft) {
                  const index = draft.findIndex((o) => o.id === updatedOpp.id);
                  if (index !== -1) {
                    draft[index] = updatedOpp;
                  }
                }
              }
            )
          );

          dispatch(
            opportunitiesApi.util.updateQueryData(
              'getOpportunities',
              { workspaceId, pipelineId: payload.pipelineId },
              (draft) => {
                if (draft) {
                  const index = draft.findIndex((o) => o.id === updatedOpp.id);
                  if (index !== -1) {
                    draft[index] = updatedOpp;
                  }
                }
              }
            )
          );

          // Update individual opportunity query
          dispatch(
            opportunitiesApi.util.updateQueryData('getOpportunity', updatedOpp.id, () => updatedOpp)
          );
        },
        RealtimeEventType.OPPORTUNITY_STAGE_CHANGED
      )
    );

    // Opportunity deleted
    unsubscribers.push(
      realtimeClient.on<OpportunityDeletedPayload>(
        topic,
        (payload) => {
          // Remove from opportunity list
          dispatch(
            opportunitiesApi.util.updateQueryData(
              'getOpportunities',
              { workspaceId },
              (draft) => {
                if (draft) {
                  const index = draft.findIndex((o) => o.id === payload.opportunityId);
                  if (index !== -1) {
                    draft.splice(index, 1);
                  }
                }
              }
            )
          );

          dispatch(
            opportunitiesApi.util.updateQueryData(
              'getOpportunities',
              { workspaceId, pipelineId: payload.pipelineId },
              (draft) => {
                if (draft) {
                  const index = draft.findIndex((o) => o.id === payload.opportunityId);
                  if (index !== -1) {
                    draft.splice(index, 1);
                  }
                }
              }
            )
          );
        },
        RealtimeEventType.OPPORTUNITY_DELETED
      )
    );

    // ========== TIME ENTRY EVENTS ==========

    // ATTENDANCE REALTIME — REMOVED.
    //
    // Three handlers here kept a CRM-local attendance cache warm from CRM's own
    // TIME_ENTRY_* events. That cache no longer exists: the clock reads Central
    // through the BFF, and the header component is driven by Central's
    // current-session response.
    //
    // Nothing replaces them on purpose. Re-adding a CRM-side attendance cache
    // is how a second source of truth comes back; the canonical invalidation
    // path is Central's availability events.

    // ========== ASSISTANT ACTION EVENTS (V2) ==========

    // Action planned
    unsubscribers.push(
      realtimeClient.on<ActionPlannedPayload>(
        topic,
        (payload) => {
          const newAction = mapActionPayloadToAction(payload.action);
          const conversationId = payload.conversationId;

          // Update conversation actions list
          dispatch(
            assistantApi.util.updateQueryData('getConversationActions', conversationId, (draft) => {
              if (draft && !draft.find((a) => a.id === newAction.id)) {
                draft.push(newAction);
              }
            })
          );

          // Update pending actions for this conversation
          dispatch(
            assistantApi.util.updateQueryData('getConversationPendingActions', conversationId, (draft) => {
              if (draft && !draft.find((a) => a.id === newAction.id)) {
                draft.push(newAction);
              }
            })
          );

          // Update workspace-level pending actions
          dispatch(
            assistantApi.util.updateQueryData('getPendingActions', undefined, (draft) => {
              if (draft && !draft.find((a) => a.id === newAction.id)) {
                draft.push(newAction);
              }
            })
          );
        },
        RealtimeEventType.ACTION_PLANNED
      )
    );

    // Action confirmed
    unsubscribers.push(
      realtimeClient.on<ActionPayload>(
        topic,
        (payload) => {
          const updatedAction = mapActionPayloadToAction(payload.action);
          const conversationId = payload.conversationId;
          updateActionInCaches(dispatch, conversationId, updatedAction);
        },
        RealtimeEventType.ACTION_CONFIRMED
      )
    );

    // Action skipped
    unsubscribers.push(
      realtimeClient.on<ActionPayload>(
        topic,
        (payload) => {
          const updatedAction = mapActionPayloadToAction(payload.action);
          const conversationId = payload.conversationId;
          updateActionInCaches(dispatch, conversationId, updatedAction);
        },
        RealtimeEventType.ACTION_SKIPPED
      )
    );

    // Action status changed
    unsubscribers.push(
      realtimeClient.on<ActionPayload>(
        topic,
        (payload) => {
          const updatedAction = mapActionPayloadToAction(payload.action);
          const conversationId = payload.conversationId;
          updateActionInCaches(dispatch, conversationId, updatedAction);
        },
        RealtimeEventType.ACTION_STATUS_CHANGED
      )
    );

    // Action completed
    unsubscribers.push(
      realtimeClient.on<ActionPayload>(
        topic,
        (payload) => {
          const updatedAction = mapActionPayloadToAction(payload.action);
          const conversationId = payload.conversationId;
          updateActionInCaches(dispatch, conversationId, updatedAction);
        },
        RealtimeEventType.ACTION_COMPLETED
      )
    );

    // Action failed
    unsubscribers.push(
      realtimeClient.on<ActionPayload>(
        topic,
        (payload) => {
          const updatedAction = mapActionPayloadToAction(payload.action);
          const conversationId = payload.conversationId;
          updateActionInCaches(dispatch, conversationId, updatedAction);
        },
        RealtimeEventType.ACTION_FAILED
      )
    );

    // ========== VOICEMAIL EVENTS ==========
    //
    // The voicemails list is filtered by phoneNumberId / status /
    // ownership / offset, which RTK Query keys individually — so
    // patching a single cache entry doesn't cover every mounted list
    // (e.g. the Phone tab + the standalone /voicemails route with a
    // different status filter). Invalidating the LIST tag is the right
    // granularity: every mounted list refetches, and unmounted ones are
    // no-ops. The network cost is a single /voicemails call per event,
    // which is cheap.
    unsubscribers.push(
      realtimeClient.on(
        topic,
        () => {
          dispatch(
            telephonyApi.util.invalidateTags([{ type: 'Voicemail', id: 'LIST' }])
          );
        },
        RealtimeEventType.VOICEMAIL_CREATED
      )
    );

    // VOICEMAIL_STATUS_CHANGED: keep unread counts / listened state in
    // sync across tabs + devices. Same invalidation strategy.
    unsubscribers.push(
      realtimeClient.on(
        topic,
        () => {
          dispatch(
            telephonyApi.util.invalidateTags([{ type: 'Voicemail', id: 'LIST' }])
          );
        },
        RealtimeEventType.VOICEMAIL_STATUS_CHANGED
      )
    );

    // ADDITIVE (Recordings tab auto-refresh). The recording webhook
    // fires this after CallSession.recordingUrl/Sid are stamped. Any
    // Recordings tab reading `useListCallSessionsQuery` (which provides
    // `CallSession` tags) will re-fetch and the row flips from
    // "Processing" to "Ready" without a manual reload. Cheap: one list
    // GET per event, only when a Recordings tab is mounted.
    unsubscribers.push(
      realtimeClient.on(
        topic,
        () => {
          dispatch(
            telephonyApi.util.invalidateTags([{ type: 'CallSession', id: 'LIST' }])
          );
        },
        RealtimeEventType.CALL_SESSION_RECORDING_READY
      )
    );

    // CRM S3 ingestion COMPLETED / FAILED — flip the Recordings tab
    // row badge from Processing → Available (or Failed). The list is
    // tagged with `CallSession` in the same LIST bucket the UNION list
    // uses, so we invalidate the same tag.
    unsubscribers.push(
      realtimeClient.on(
        topic,
        () => {
          dispatch(
            telephonyApi.util.invalidateTags([{ type: 'CallSession', id: 'LIST' }])
          );
        },
        RealtimeEventType.CALL_RECORDING_INGESTION_COMPLETED,
      ),
    );
    unsubscribers.push(
      realtimeClient.on(
        topic,
        () => {
          dispatch(
            telephonyApi.util.invalidateTags([{ type: 'CallSession', id: 'LIST' }])
          );
        },
        RealtimeEventType.CALL_RECORDING_INGESTION_FAILED,
      ),
    );

    // ========== CONTACT EVENTS (PR-27.2) ==========
    //
    // Contacts can land in CRM through three different paths:
    //
    //   1. User Add Contact via the CRM UI (already wired via RTK
    //      Query mutation invalidation in contactsApi.ts).
    //   2. Sibling-app sync (Outbound, Scheduler, WeOpen, PatchProof)
    //      through `/api/internal/contacts/*` — bypasses the CRM
    //      frontend entirely.
    //   3. CRM-side admin tooling.
    //
    // Without a realtime listener the (2) and (3) paths silently land
    // in the database and the user has to refresh the Contacts tab to
    // see them. Subscribing the workspace-scoped contact events and
    // invalidating the `{Contact, id: workspaceId}` tag triggers RTK
    // Query to refetch any mounted Contacts query (which is exactly
    // what `useGetContactsQuery` providers on contactsApi.ts).
    //
    // We invalidate BOTH the workspace-scoped LIST id AND the literal
    // 'LIST' id so a query that was opened without a workspaceId param
    // (defensive) still refetches.
    const invalidateContactList = () => {
      dispatch(
        contactsApi.util.invalidateTags([
          { type: 'Contact', id: workspaceId },
          { type: 'Contact', id: 'LIST' },
        ])
      );
    };

    unsubscribers.push(
      realtimeClient.on(topic, invalidateContactList, RealtimeEventType.CONTACT_CREATED),
    );
    unsubscribers.push(
      realtimeClient.on(topic, invalidateContactList, RealtimeEventType.CONTACT_UPDATED),
    );
    unsubscribers.push(
      realtimeClient.on(topic, invalidateContactList, RealtimeEventType.CONTACT_DELETED),
    );

    // ========== PROVIDER MIRROR EVENTS (Phase 4) ==========
    // Any successful upsert/delete of an Outbound-mirrored
    // IntegrationConnection row triggers a workspace fan-out. Every
    // open CRM session in this workspace invalidates its integration
    // list — the Providers page + pre-call provider selector then
    // refetch from the safe read endpoint. Payload carries only ids
    // + change kind (no credentials/tokens). This is provider
    // synchronization ONLY; the event NEVER indicates a call or
    // recording was created.
    const invalidateIntegrationList = () => {
      dispatch(
        integrationsApi.util.invalidateTags([
          { type: 'Integration', id: 'LIST' },
          // Settings → Providers (Central list): its recording modes come
          // from this copy. Additive — LIST is invalidated exactly as before.
          { type: 'Integration', id: 'SETTINGS_PROVIDERS' },
        ]),
      );
    };
    unsubscribers.push(
      realtimeClient.on(
        topic,
        invalidateIntegrationList,
        RealtimeEventType.PROVIDER_MIRROR_UPDATED,
      ),
    );

    // Phase 12.4 — Circle ↔ Outbound Provider affinity.
    // When a Head adds or removes a provider on any Circle in this
    // workspace, invalidate the affected Circle's provider tag so any
    // open Circle Providers tab refetches from the safe read endpoint.
    // Payload is metadata-only (workspaceId + circleId + provider id +
    // changeKind); never carries credentials.
    const invalidateCircleProviders = (data: unknown) => {
      const payload = data as { circleId?: string } | undefined;
      if (payload && typeof payload.circleId === 'string') {
        dispatch(
          circlesApi.util.invalidateTags([
            { type: 'Circle', id: `${payload.circleId}-providers` },
          ]),
        );
      }
    };
    unsubscribers.push(
      realtimeClient.on(
        topic,
        invalidateCircleProviders,
        RealtimeEventType.PROVIDER_AFFINITY_UPDATED,
      ),
    );

    // Phase 12.7 — Circle → People per-member provider pin.
    // When a Head add/update/removes a pin, invalidate the affected
    // Circle's members-provider tag (bulk) + the specific
    // (circle, user) tag if we have the userId.
    const invalidateCircleMembersProviders = (data: unknown) => {
      const payload = data as { circleId?: string; userId?: string } | undefined;
      if (!payload || typeof payload.circleId !== 'string') return;
      const tags: Array<{ type: 'Circle'; id: string }> = [
        { type: 'Circle', id: `${payload.circleId}-people-providers` },
      ];
      if (typeof payload.userId === 'string' && payload.userId.length > 0) {
        tags.push({
          type: 'Circle',
          id: `${payload.circleId}-people-providers-${payload.userId}`,
        });
      }
      dispatch(circlesApi.util.invalidateTags(tags));
    };
    unsubscribers.push(
      realtimeClient.on(
        topic,
        invalidateCircleMembersProviders,
        RealtimeEventType.PROVIDER_USER_ASSIGNMENT_UPDATED,
      ),
    );

    // Cleanup
    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [workspaceId, dispatch]);
}

// ============================================================
// Conversation-level Cache Updates Hook
// ============================================================

/**
 * Hook for conversation-specific cache updates.
 * Call this when viewing a specific conversation.
 */
export function useConversationCacheUpdates(conversationId: string | null | undefined) {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!conversationId) return;

    const topic = `conversation:${conversationId}`;
    const unsubscribers: Array<() => void> = [];

    // New message
    unsubscribers.push(
      realtimeClient.on<ConversationMessagePayload>(
        topic,
        (payload) => {
          const newMessage = mapPayloadToMessage(payload);

          // Update conversation detail query
          dispatch(
            conversationsApi.util.updateQueryData(
              'getConversationDetail',
              conversationId,
              (draft) => {
                if (draft && draft.messages && !draft.messages.find((m) => m.id === newMessage.id)) {
                  draft.messages.push(newMessage);
                }
              }
            )
          );
        },
        RealtimeEventType.CONVERSATION_MESSAGE_CREATED
      )
    );

    // Action planned (conversation-level subscription)
    unsubscribers.push(
      realtimeClient.on<ActionPlannedPayload>(
        topic,
        (payload) => {
          const newAction = mapActionPayloadToAction(payload.action);

          // Update conversation detail's actions array
          dispatch(
            conversationsApi.util.updateQueryData(
              'getConversationDetail',
              conversationId,
              (draft) => {
                if (draft && draft.actions && !draft.actions.find((a) => a.id === newAction.id)) {
                  draft.actions.push(newAction);
                }
              }
            )
          );

          // Also update the dedicated actions queries
          dispatch(
            assistantApi.util.updateQueryData('getConversationActions', conversationId, (draft) => {
              if (draft && !draft.find((a) => a.id === newAction.id)) {
                draft.push(newAction);
              }
            })
          );

          dispatch(
            assistantApi.util.updateQueryData('getConversationPendingActions', conversationId, (draft) => {
              if (draft && !draft.find((a) => a.id === newAction.id)) {
                draft.push(newAction);
              }
            })
          );
        },
        RealtimeEventType.ACTION_PLANNED
      )
    );

    // Action status changes (conversation-level)
    const actionStatusEvents = [
      RealtimeEventType.ACTION_CONFIRMED,
      RealtimeEventType.ACTION_SKIPPED,
      RealtimeEventType.ACTION_STATUS_CHANGED,
      RealtimeEventType.ACTION_COMPLETED,
      RealtimeEventType.ACTION_FAILED,
    ] as const;

    actionStatusEvents.forEach((eventType) => {
      unsubscribers.push(
        realtimeClient.on<ActionPayload>(
          topic,
          (payload) => {
            const updatedAction = mapActionPayloadToAction(payload.action);

            // Update conversation detail's actions array
            dispatch(
              conversationsApi.util.updateQueryData(
                'getConversationDetail',
                conversationId,
                (draft) => {
                  if (draft && draft.actions) {
                    const index = draft.actions.findIndex((a) => a.id === updatedAction.id);
                    if (index !== -1) {
                      draft.actions[index] = updatedAction;
                    }
                  }
                }
              )
            );

            // Also update the dedicated actions queries
            updateActionInCaches(dispatch, conversationId, updatedAction);
          },
          eventType
        )
      );
    });

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [conversationId, dispatch]);
}

// ============================================================
// Helper Functions
// ============================================================

/**
 * Update an action across all relevant caches
 */
function updateActionInCaches(
  dispatch: ReturnType<typeof useAppDispatch>,
  conversationId: string,
  updatedAction: ConversationAction
) {
  // Update conversation actions list
  dispatch(
    assistantApi.util.updateQueryData('getConversationActions', conversationId, (draft) => {
      if (draft) {
        const index = draft.findIndex((a) => a.id === updatedAction.id);
        if (index !== -1) {
          draft[index] = updatedAction;
        }
      }
    })
  );

  // Update pending actions for this conversation
  dispatch(
    assistantApi.util.updateQueryData('getConversationPendingActions', conversationId, (draft) => {
      if (draft) {
        const index = draft.findIndex((a) => a.id === updatedAction.id);
        if (index !== -1) {
          // If action is no longer pending, remove it from pending list
          const pendingStatuses = ['pending', 'pending_confirmation', 'queued'];
          if (!pendingStatuses.includes(updatedAction.actionStatus || '')) {
            draft.splice(index, 1);
          } else {
            draft[index] = updatedAction;
          }
        }
      }
    })
  );

  // Update workspace-level pending actions
  dispatch(
    assistantApi.util.updateQueryData('getPendingActions', undefined, (draft) => {
      if (draft) {
        const index = draft.findIndex((a) => a.id === updatedAction.id);
        if (index !== -1) {
          // If action is no longer pending, remove it
          const pendingStatuses = ['pending', 'pending_confirmation', 'queued'];
          if (!pendingStatuses.includes(updatedAction.actionStatus || '')) {
            draft.splice(index, 1);
          } else {
            draft[index] = updatedAction;
          }
        }
      }
    })
  );
}
